export type SentryEnvironment = "development" | "staging" | "production";

type SentryRuntimeConfigInput = {
  captureConsole?: string;
  dsn?: string;
  enabledFlag?: string | boolean;
  environment: SentryEnvironment;
  release?: string;
  replaysOnErrorSampleRate?: string;
  replaysSessionSampleRate?: string;
  tracesSampleRate?: string;
};

export type SentryRuntimeConfig = {
  captureConsole: boolean;
  enabled: boolean;
  release?: string;
  replaysOnErrorSampleRate: number;
  replaysSessionSampleRate: number;
  tracesSampleRate: number;
};

const DEFAULT_SAMPLE_RATES: Record<
  SentryEnvironment,
  Pick<
    SentryRuntimeConfig,
    | "replaysOnErrorSampleRate"
    | "replaysSessionSampleRate"
    | "tracesSampleRate"
  >
> = {
  development: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
  },
  staging: {
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1,
  },
  production: {
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1,
  },
};

export const isTruthyEnvValue = (value?: string | boolean) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "string") {
    return false;
  }

  const normalized = value.trim().toLowerCase();

  return normalized !== "" && normalized !== "false" && normalized !== "0";
};

export const parseSampleRate = (
  value: string | undefined,
  fallback: number
) => {
  if (value == null || value.trim() === "") {
    return fallback;
  }

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 1) {
    return fallback;
  }

  return parsedValue;
};

export const resolveSentryRuntimeConfig = ({
  captureConsole,
  dsn,
  enabledFlag,
  environment,
  release,
  replaysOnErrorSampleRate,
  replaysSessionSampleRate,
  tracesSampleRate,
}: SentryRuntimeConfigInput): SentryRuntimeConfig => {
  const defaultSampleRates = DEFAULT_SAMPLE_RATES[environment];
  const trimmedDsn = dsn?.trim();
  const trimmedRelease = release?.trim();

  const enabled =
    isTruthyEnvValue(enabledFlag) &&
    ["staging", "production"].includes(environment) &&
    Boolean(trimmedDsn);

  return {
    enabled,
    release: trimmedRelease || undefined,
    captureConsole: isTruthyEnvValue(captureConsole),
    tracesSampleRate: parseSampleRate(
      tracesSampleRate,
      defaultSampleRates.tracesSampleRate
    ),
    replaysSessionSampleRate: parseSampleRate(
      replaysSessionSampleRate,
      defaultSampleRates.replaysSessionSampleRate
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      replaysOnErrorSampleRate,
      defaultSampleRates.replaysOnErrorSampleRate
    ),
  };
};
